import { FC, PropsWithChildren, Component, ErrorInfo, ReactNode, useEffect } from "react";

import { FlagProvider, IConfig, useUnleashContext } from "@unleash/proxy-client-react";
import { useGetUserDetailsQuery } from "@store/services/user";
import { getIsStagingEnvironment } from "@utils";

import { isStartUnleashClient } from "./UnleashProvider.utilities";

const unleashKey = import.meta.env.VITE_UNLEASH_KEY;
const unleashUrl = import.meta.env.VITE_UNLEASH_URL ?? "https://eu.app.unleash-hosted.com/eucc0001/api/frontend";
const isCypress = import.meta.env.MODE === "cypress";

const unleashDevelopmentKey = "TestaViva:development.ababbb6349262d9c3b4aa04a5388b3f96d1fbb672d4d2a4ddba076d8";

const getBootstrap = () => {
  if (!isCypress || typeof window === "undefined") {
    return [];
  }
  try {
    return JSON.parse(window.localStorage.getItem("__test_flags__") ?? "[]");
  } catch {
    return [];
  }
};

const config: IConfig = {
  url: unleashUrl,
  // Temporary fix for the Unleash staging environment until we have a proper staging environment variable setup.
  clientKey: getIsStagingEnvironment() ? unleashDevelopmentKey : (unleashKey ?? "disabled"),
  refreshInterval: 60 * 60,
  appName: "ApplicationSPA",
  bootstrap: getBootstrap(),
};

class UnleashErrorBoundary extends Component<PropsWithChildren<{ fallback: ReactNode }>, { hasError: boolean }> {
  constructor(properties: PropsWithChildren<{ fallback: ReactNode }>) {
    super(properties);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Unleash provider error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      try {
        return (
          <FlagProvider
            config={{
              url: config.url,
              clientKey: "disabled",
              bootstrap: [],
              appName: config.appName,
            }}
            startClient={false}
          >
            {this.props.fallback}
          </FlagProvider>
        );
      } catch {
        return this.props.fallback;
      }
    }

    return this.props.children;
  }
}

const UnleashContextUpdater: FC<PropsWithChildren> = ({ children }) => {
  const { data: userDetails } = useGetUserDetailsQuery();
  const updateContext = useUnleashContext();

  useEffect(() => {
    if (userDetails?.id == null) {
      return;
    }
    updateContext({ userId: String(userDetails.id) });
  }, [userDetails?.id, updateContext]);

  return <>{children}</>;
};

const UnleashProvider: FC<PropsWithChildren> = ({ children }) => {
  const shouldStartClient = isStartUnleashClient;

  return (
    <UnleashErrorBoundary fallback={children}>
      <FlagProvider config={config} startClient={shouldStartClient}>
        <UnleashContextUpdater>{children}</UnleashContextUpdater>
      </FlagProvider>
    </UnleashErrorBoundary>
  );
};

export default UnleashProvider;
